import { apiClient } from './ApiClient'
import { scopedTo } from './activeStartup'
import type {
  InterviewSession,
  InterviewMessage,
  InterviewResponse,
} from '@/types/api'

export type InterviewFocusDomain =
  | 'business_model'
  | 'product'
  | 'go_to_market'
  | 'team'

/** Body accepted by POST .../interviews (yaml:1096). */
export interface CreateInterviewSessionInput {
  focusDomain?: InterviewFocusDomain
  maxGaps?: number
  /**
   * Target exactly these gaps (1–20). They must be open and not held by
   * another session, otherwise the create call fails.
   */
  gapIds?: string[]
}

/** Body accepted by POST .../messages (yaml:1155). */
export interface SendInterviewMessageInput {
  content: string
  activeGapId?: string
  clientTimestamp?: string
}

const scopedPath = (startupId: string) =>
  `/v1/startups/${encodeURIComponent(startupId)}/interviews`

/**
 * Wraps /v1/startups/{startupId}/interviews. The startup-scoped methods are
 * the canonical interview surface for Phase 2+ flows. Legacy unscoped
 * methods are kept below for pages still under the ESLint exempt list —
 * they are renamed with a `legacy` prefix to free the clean names for the
 * new signatures, and get extracted in a later commit.
 */
export class InterviewService {
  // ─── Startup-scoped surface (Phase 1 onwards) ──────────────────────

  /** POST .../interviews — create a new interview session. */
  async createSession(
    startupId: string,
    opts?: CreateInterviewSessionInput
  ): Promise<InterviewResponse> {
    return apiClient.post<InterviewResponse>(scopedPath(startupId), opts ?? {})
  }

  /** POST .../interviews/{sessionId}/messages — send a user message. */
  async sendMessage(
    startupId: string,
    sessionId: string,
    msg: SendInterviewMessageInput
  ): Promise<InterviewResponse> {
    const { content, activeGapId, clientTimestamp } = msg
    return apiClient.post<InterviewResponse>(
      `${scopedPath(startupId)}/${encodeURIComponent(sessionId)}/messages`,
      { content, activeGapId, clientTimestamp }
    )
  }

  /** GET .../interviews/{sessionId}/history — unwraps { messages }. */
  async getHistory(
    startupId: string,
    sessionId: string
  ): Promise<InterviewMessage[]> {
    const res = await apiClient.get<{ messages?: InterviewMessage[] }>(
      `${scopedPath(startupId)}/${encodeURIComponent(sessionId)}/history`
    )
    return res.messages ?? []
  }

  /** POST .../interviews/{sessionId}/end — close the session. */
  async endSession(startupId: string, sessionId: string): Promise<void> {
    await apiClient.post(
      `${scopedPath(startupId)}/${encodeURIComponent(sessionId)}/end`
    )
  }

  // ─── Legacy (unscoped) methods ───────────────────────────────────────
  // Hit /v1/interviews/* which no longer exists on the backend as of
  // PR #66. Kept callable so legacy pages still compile.
  // ─────────────────────────────────────────────────────────────────────

  /** @deprecated Unscoped session list — no replacement on the new surface. */
  async listSessions(): Promise<InterviewSession[]> {
    const response = await apiClient.get<{ sessions: InterviewSession[] }>(scopedTo('/interviews'))
    return response.sessions || []
  }

  /** @deprecated Use `createSession(startupId, opts?)` instead. */
  async startSession(opts?: CreateInterviewSessionInput): Promise<InterviewResponse> {
    return apiClient.post<InterviewResponse>(scopedTo('/interviews'), opts ?? {})
  }

  /** @deprecated Use `sendMessage(startupId, sessionId, msg)` instead. */
  async legacySendMessage(
    sessionId: string,
    content: string,
    activeGapId?: string,
    clientTimestamp?: string
  ): Promise<InterviewResponse> {
    return apiClient.post<InterviewResponse>(scopedTo(`/interviews/${sessionId}/messages`), {
      content,
      activeGapId,
      clientTimestamp,
    })
  }

  /** @deprecated No replacement — use useStartupBootstrap + chat store instead. */
  async getSession(sessionId: string): Promise<InterviewSession | null> {
    const response = await apiClient.get<{ session: InterviewSession }>(
      scopedTo(`/interviews/${sessionId}`)
    )
    return response.session || null
  }

  /** @deprecated Use `getHistory(startupId, sessionId)` instead. */
  async legacyGetHistory(sessionId: string): Promise<InterviewMessage[]> {
    const response = await apiClient.get<{ messages: InterviewMessage[] }>(
      scopedTo(`/interviews/${sessionId}/history`)
    )
    return response.messages || []
  }

  /** @deprecated Use `endSession(startupId, sessionId)` instead. */
  async legacyEndSession(sessionId: string): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(scopedTo(`/interviews/${sessionId}/end`))
  }
}

export const interviewService = new InterviewService()
